import type { GitHunk } from "gitpod-next-api/supervisor/v1/environmentopts_pb";
import { useMemo, useState, useCallback, type FC } from "react";
import { PatchDiff, MultiFileDiff } from "@pierre/diffs/react";
import { registerCustomTheme, type SelectedLineRange, type DiffLineAnnotation, type FileContents } from "@pierre/diffs";
import {
    convertHunksToPatch,
    computeContentHashForLines,
    computeContentHash,
    isLineInHunk,
} from "@/routes/environments/code-changes/diff-utils";
import { InlineCommentAnnotation } from "@/routes/environments/code-changes/InlineCommentAnnotation";
import type { ReviewComment } from "@/components/ai/review/types";
import pierreLightBase from "@/routes/environments/code-changes/themes/pierre-light.json";
import pierreDarkBase from "@/routes/environments/code-changes/themes/pierre-dark.json";
import { useTheme } from "@/hooks/use-theme";
import { isTouchDevice } from "@/utils/is-touch-device";

// Surface primary colors from index.css
// Light: --surface-primary: var(--gray-200) = 246 246 246 = #f6f6f6
// Dark: --surface-primary: var(--gray-900) = 26 26 26 = #1a1a1a

registerCustomTheme("ona-light", () =>
    Promise.resolve({
        ...pierreLightBase,
        name: "ona-light",
        type: "light" as const,
        colors: {
            ...pierreLightBase.colors,
            "editor.background": "#f6f6f6",
        },
    }),
);

registerCustomTheme("ona-dark", () =>
    Promise.resolve({
        ...pierreDarkBase,
        name: "ona-dark",
        type: "dark" as const,
        colors: {
            ...pierreDarkBase.colors,
            "editor.background": "#1a1a1a",
        },
    }),
);

export type LineSelection = {
    startLine: number;
    endLine: number;
    startIndex: number;
    endIndex: number;
    side?: "deletions" | "additions";
};

type AnnotationMetadata = {
    reviewComment?: ReviewComment;
    startLine: number;
    endLine: number;
};

export type GitDiffViewProps = {
    hunks: GitHunk[];
    filePath?: string;
    onLineSelect?: (selection: LineSelection) => void;
    comments?: ReviewComment[];
    onAddComment?: (
        content: string,
        startLine: number,
        endLine: number,
        side?: "deletions" | "additions",
        contentHash?: string,
    ) => void;
    onUpdateComment?: (reviewComment: ReviewComment, newContent: string) => void;
    onDeleteComment?: (reviewComment: ReviewComment) => void;
    onSetCommentThumbsUp?: (reviewComment: ReviewComment, thumbsUp: boolean) => void;
    onSetCommentThumbsDown?: (reviewComment: ReviewComment, thumbsDown: boolean) => void;
};

const GitDiffView: FC<GitDiffViewProps> = ({
    hunks,
    filePath = "file",
    comments,
    onAddComment,
    onUpdateComment,
    onDeleteComment,
    onSetCommentThumbsUp,
    onSetCommentThumbsDown,
}) => {
    const [commentInputAnnotation, setCommentInputAnnotation] = useState<DiffLineAnnotation<AnnotationMetadata> | null>(
        null,
    );

    // For touch devices: track pending selection that needs a second tap to confirm
    // pending touch selection stores the initial selection
    const [pendingTouchSelection, setPendingTouchSelection] = useState<SelectedLineRange | null>(null);
    // final touch selection stores the final selection line range after the 2-tap is complete
    const [finalTouchSelection, setFinalTouchSelection] = useState<SelectedLineRange | null>(null);

    const { effectiveTheme } = useTheme();
    const patch = useMemo(() => convertHunksToPatch(hunks, filePath), [hunks, filePath]);

    const createAnnotationFromRange = (range: SelectedLineRange) => {
        setCommentInputAnnotation({
            side: range.endSide ?? range.side ?? "additions",
            lineNumber: range.end,
            metadata: {
                startLine: range.start,
                endLine: range.end,
            },
        });
        setPendingTouchSelection(null);
    };

    const handleLineSelectionStart = (range: SelectedLineRange | null) => {
        if (range && pendingTouchSelection && isTouchDevice()) {
            const combinedRange: SelectedLineRange = {
                start: pendingTouchSelection.start,
                end: range.start,
                side: pendingTouchSelection.side,
                endSide: range.side === pendingTouchSelection.side ? undefined : range.side,
            };
            // normally, selection end triggers after selection start
            // but setting state somehow doesnt selection end
            // so need to create annotation from here
            setPendingTouchSelection(null);
            setFinalTouchSelection(combinedRange);
            createAnnotationFromRange(combinedRange);
        }
    };

    const handleLineSelectionEnd = (range: SelectedLineRange | null) => {
        // On touch devices: require two taps to create annotation
        // First tap records start line, second tap records end line
        if (isTouchDevice()) {
            if (finalTouchSelection) {
                createAnnotationFromRange(finalTouchSelection);
            } else if (pendingTouchSelection && !range) {
                // Second tap on same line clears selection (range is null)
                // Treat this as selecting the same line for start and end
                createAnnotationFromRange(pendingTouchSelection);
            } else if (range) {
                // First tap: just highlight the line, wait for second tap
                setPendingTouchSelection(range);
            }
        } else {
            // Desktop: create annotation immediately on selection end (drag release)
            if (range) {
                createAnnotationFromRange(range);
            }
        }
    };

    const handleCommentSubmit = (content: string) => {
        if (commentInputAnnotation?.metadata) {
            const { startLine, endLine } = commentInputAnnotation.metadata;
            const side = commentInputAnnotation.side;
            // Compute content hash for outdated detection
            const contentHash = computeContentHashForLines(hunks, startLine, endLine, side) ?? undefined;
            onAddComment?.(content, startLine, endLine, side, contentHash);
            setCommentInputAnnotation(null);
            setPendingTouchSelection(null);
            setFinalTouchSelection(null);
        }
    };

    const handleCommentCancel = () => {
        setCommentInputAnnotation(null);
        setPendingTouchSelection(null);
        setFinalTouchSelection(null);
    };

    // Create line annotations for comments and pending comment input
    const lineAnnotations = useMemo<DiffLineAnnotation<AnnotationMetadata>[]>(() => {
        const annotations: DiffLineAnnotation<AnnotationMetadata>[] = [];

        // map comments to line annotations
        if (comments) {
            for (const comment of comments) {
                annotations.push({
                    side: comment.side ?? "additions",
                    lineNumber: comment.endLine,
                    metadata: {
                        reviewComment: comment,
                        startLine: comment.startLine,
                        endLine: comment.endLine,
                    },
                });
            }
        }

        if (commentInputAnnotation) {
            annotations.push(commentInputAnnotation);
        }

        return annotations;
    }, [comments, commentInputAnnotation]);

    const handleCommentUpdate = (comment: ReviewComment) => (newContent: string) => {
        onUpdateComment?.(comment, newContent);
    };

    const handleCommentDelete = (comment: ReviewComment) => () => {
        onDeleteComment?.(comment);
    };

    const renderAnnotation = (annotation: DiffLineAnnotation<AnnotationMetadata>) => {
        const { startLine, endLine, reviewComment } = annotation.metadata;

        // Check if comment is outdated by comparing stored hash with current content hash
        let isOutdated = false;
        if (reviewComment?.contentHash) {
            const currentHash = computeContentHashForLines(
                hunks,
                startLine,
                endLine,
                reviewComment.side ?? "additions",
            );
            isOutdated = currentHash !== null && currentHash !== reviewComment.contentHash;
        }

        return (
            <InlineCommentAnnotation
                startLine={startLine}
                endLine={endLine}
                defaultComment={reviewComment?.content ?? ""}
                // when no review comment is associated with this annotation
                // it means this is a draft comment, so should be default editable
                defaultEditable={!reviewComment}
                isOutdated={isOutdated}
                author={reviewComment?.author}
                commentId={reviewComment?.id}
                filePath={filePath}
                thumbsUp={reviewComment?.thumbsUp}
                thumbsDown={reviewComment?.thumbsDown}
                onSubmit={reviewComment ? handleCommentUpdate(reviewComment) : handleCommentSubmit}
                onCancel={handleCommentCancel}
                onDelete={reviewComment ? handleCommentDelete(reviewComment) : undefined}
                onThumbsUp={
                    reviewComment && onSetCommentThumbsUp
                        ? (thumbsUp) => onSetCommentThumbsUp(reviewComment, thumbsUp)
                        : undefined
                }
                onThumbsDown={
                    reviewComment && onSetCommentThumbsDown
                        ? (thumbsDown) => onSetCommentThumbsDown(reviewComment, thumbsDown)
                        : undefined
                }
            />
        );
    };

    if (!patch) {
        return null;
    }

    let selectedLines: SelectedLineRange | null | undefined;
    if (pendingTouchSelection) {
        selectedLines = pendingTouchSelection;
    } else if (finalTouchSelection) {
        selectedLines = finalTouchSelection;
    } else if (commentInputAnnotation) {
        selectedLines = undefined;
    } else {
        selectedLines = null;
    }

    return (
        <PatchDiff<AnnotationMetadata>
            patch={patch}
            options={{
                theme: { dark: "ona-dark", light: "ona-light" },
                themeType: effectiveTheme,
                diffStyle: "unified",
                diffIndicators: "bars",
                hunkSeparators: "simple",
                expandUnchanged: false,
                disableFileHeader: true,
                overflow: "scroll",
                enableLineSelection: !!onAddComment,
                enableHoverUtility: true,
                onLineSelectionStart: handleLineSelectionStart,
                onLineSelectionEnd: onAddComment ? handleLineSelectionEnd : undefined,
                // Enable drag-to-select on mobile by preventing browser gesture handling
                unsafeCSS: `[data-interactive-line-numbers] [data-column-number] { touch-action: none; }`,
            }}
            // selectedLines logic:
            // - If there's a comment input annotation, let the component handle highlights (undefined)
            // - If there's a pending touch selection, show that selection highlighted
            // - Otherwise, clear all highlights (null) to ensure cancelling clears them
            selectedLines={selectedLines}
            lineAnnotations={lineAnnotations}
            renderAnnotation={renderAnnotation}
            renderHoverUtility={() => (
                <button className="size-5 rounded-md bg-surface-primary-inverted font-mono text-base text-content-primary-inverted">
                    +
                </button>
            )}
            // Styling via CSS variables per https://diffs.com/docs#styling
            style={
                {
                    "--diffs-font-family": "var(--font-family-mono)",
                    "--diffs-gap-block": "0px",
                } as React.CSSProperties
            }
        />
    );
};

export type ContentDiffViewProps = {
    originalContent: string;
    newContent: string;
    filePath?: string;
    /** Backend-provided hunks for determining which lines are changed */
    hunks: GitHunk[];
    comments?: ReviewComment[];
    onAddComment?: (
        content: string,
        startLine: number,
        endLine: number,
        side?: "deletions" | "additions",
        contentHash?: string,
    ) => void;
    onUpdateComment?: (reviewComment: ReviewComment, newContent: string) => void;
    onDeleteComment?: (reviewComment: ReviewComment) => void;
    onSetCommentThumbsUp?: (reviewComment: ReviewComment, thumbsUp: boolean) => void;
    onSetCommentThumbsDown?: (reviewComment: ReviewComment, thumbsDown: boolean) => void;
};

const ContentDiffView: FC<ContentDiffViewProps> = ({
    originalContent,
    newContent,
    filePath = "file",
    hunks,
    comments,
    onAddComment,
    onUpdateComment,
    onDeleteComment,
    onSetCommentThumbsUp,
    onSetCommentThumbsDown,
}) => {
    const [commentInputAnnotation, setCommentInputAnnotation] = useState<DiffLineAnnotation<AnnotationMetadata> | null>(
        null,
    );
    const { effectiveTheme } = useTheme();

    const oldFile: FileContents = useMemo(
        () => ({
            name: filePath,
            contents: originalContent,
        }),
        [filePath, originalContent],
    );

    const newFile: FileContents = useMemo(
        () => ({
            name: filePath,
            contents: newContent,
        }),
        [filePath, newContent],
    );

    const handleLineSelectionEnd = useCallback((range: SelectedLineRange | null) => {
        if (range) {
            setCommentInputAnnotation({
                side: range.endSide ?? range.side ?? "additions",
                lineNumber: range.end,
                metadata: {
                    startLine: range.start,
                    endLine: range.end,
                },
            });
        }
    }, []);

    const handleCommentSubmit = (content: string) => {
        if (commentInputAnnotation?.metadata) {
            const { startLine, endLine } = commentInputAnnotation.metadata;
            const side = commentInputAnnotation.side;
            // Compute content hash for outdated detection
            // Use originalContent for deletions side, newContent for additions side
            const sourceContent = side === "deletions" ? originalContent : newContent;
            const contentHash = computeContentHash(sourceContent, startLine, endLine) ?? undefined;
            onAddComment?.(content, startLine, endLine, side, contentHash);
            setCommentInputAnnotation(null);
        }
    };

    const handleCommentCancel = () => {
        setCommentInputAnnotation(null);
    };

    // Create line annotations for comments and pending comment input
    const lineAnnotations = useMemo<DiffLineAnnotation<AnnotationMetadata>[]>(() => {
        const annotations: DiffLineAnnotation<AnnotationMetadata>[] = [];

        // map comments to line annotations
        if (comments) {
            for (const comment of comments) {
                annotations.push({
                    side: comment.side ?? "additions",
                    lineNumber: comment.endLine,
                    metadata: {
                        reviewComment: comment,
                        startLine: comment.startLine,
                        endLine: comment.endLine,
                    },
                });
            }
        }

        if (commentInputAnnotation) {
            annotations.push(commentInputAnnotation);
        }

        return annotations;
    }, [comments, commentInputAnnotation]);

    const handleCommentUpdate = (comment: ReviewComment) => (updatedContent: string) => {
        onUpdateComment?.(comment, updatedContent);
    };

    const handleCommentDelete = (comment: ReviewComment) => () => {
        onDeleteComment?.(comment);
    };

    const renderAnnotation = (annotation: DiffLineAnnotation<AnnotationMetadata>) => {
        const { startLine, endLine, reviewComment } = annotation.metadata;

        // Check if comment is outdated by comparing stored hash with current content hash
        // Use originalContent for deletions side, newContent for additions side
        let isOutdated = false;
        if (reviewComment?.contentHash) {
            const sourceContent = reviewComment.side === "deletions" ? originalContent : newContent;
            const currentHash = computeContentHash(sourceContent, startLine, endLine);
            isOutdated = currentHash !== null && currentHash !== reviewComment.contentHash;
        }

        return (
            <InlineCommentAnnotation
                startLine={startLine}
                endLine={endLine}
                defaultComment={reviewComment?.content ?? ""}
                defaultEditable={!reviewComment}
                isOutdated={isOutdated}
                author={reviewComment?.author}
                commentId={reviewComment?.id}
                filePath={filePath}
                thumbsUp={reviewComment?.thumbsUp}
                thumbsDown={reviewComment?.thumbsDown}
                onSubmit={reviewComment ? handleCommentUpdate(reviewComment) : handleCommentSubmit}
                onCancel={handleCommentCancel}
                onDelete={reviewComment ? handleCommentDelete(reviewComment) : undefined}
                onThumbsUp={
                    reviewComment && onSetCommentThumbsUp
                        ? (thumbsUp) => onSetCommentThumbsUp(reviewComment, thumbsUp)
                        : undefined
                }
                onThumbsDown={
                    reviewComment && onSetCommentThumbsDown
                        ? (thumbsDown) => onSetCommentThumbsDown(reviewComment, thumbsDown)
                        : undefined
                }
            />
        );
    };

    // Only expand unchanged sections if there are comments outside the diff hunks.
    // This avoids unnecessary expansion when all comments are on changed lines.
    const hasCommentsOutsideHunks = useMemo(() => {
        if (!comments || comments.length === 0) {
            return false;
        }

        const hunkRanges = hunks.map((h) => ({
            startLine: h.newStartLine,
            endLine: h.newStartLine + h.newLines - 1,
        }));

        return comments.some((comment) => {
            // Check if any line in the comment range is outside all hunks
            for (let line = comment.startLine; line <= comment.endLine; line++) {
                if (!isLineInHunk(line, hunkRanges)) {
                    return true;
                }
            }
            return false;
        });
    }, [comments, hunks]);

    return (
        <MultiFileDiff<AnnotationMetadata>
            oldFile={oldFile}
            newFile={newFile}
            options={{
                theme: { dark: "ona-dark", light: "ona-light" },
                themeType: effectiveTheme,
                diffStyle: "unified",
                diffIndicators: "bars",
                hunkSeparators: "line-info",
                expandUnchanged: hasCommentsOutsideHunks,
                disableFileHeader: true,
                overflow: "scroll",
                enableLineSelection: !!onAddComment,
                onLineSelectionEnd: onAddComment ? handleLineSelectionEnd : undefined,
            }}
            selectedLines={commentInputAnnotation ? undefined : null}
            lineAnnotations={lineAnnotations}
            renderAnnotation={renderAnnotation}
            style={
                {
                    "--diffs-font-family": "var(--font-family-mono)",
                } as React.CSSProperties
            }
        />
    );
};

export { GitDiffView, ContentDiffView };
